const initCardCarouselRuntime = () => {
  if (window.__waldCardCarouselInitialized) return;
  window.__waldCardCarouselInitialized = true;

  const carouselSelector = '[data-card-carousel]';
  const hoverCapableMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const carouselStates = new WeakMap();
  const AUTO_CYCLE_INTERVAL_MS = 1000;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  // Announce the first *deliberate* slideshow navigation — a swipe, an arrow
  // click, or a horizontal wheel — never the passive hover auto-cycle. Other
  // features (e.g. the collection quick-view teaching label) listen for this to
  // know the shopper has understood the card slider. Fires once per page load;
  // a cheap no-op on every deliberate nav after the first.
  let slideshowUseAnnounced = false;
  const announceSlideshowUse = () => {
    if (slideshowUseAnnounced) return;
    slideshowUseAnnounced = true;
    document.dispatchEvent(new CustomEvent('wald:slideshow-used'));
  };

  // Fast, smooth slide transition between images. Replaces the browser's native
  // smooth-scroll (slower + occasionally janky on scroll-snap tracks) with a
  // short JS-driven tween, so the switch between slides is quick and consistent.
  const SLIDE_DURATION_MS = 200;
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const animateScroll = (state, to, onComplete) => {
    const track = state.track;
    if (state.scrollAnim) {
      window.cancelAnimationFrame(state.scrollAnim);
      state.scrollAnim = null;
    }
    const from = track.scrollLeft;
    const distance = to - from;
    const finish = () => {
      if (typeof onComplete === 'function') onComplete();
      else state.isProgrammaticScroll = false;
    };
    if (Math.abs(distance) < 1) {
      track.scrollLeft = to;
      finish();
      return;
    }
    const startTime = performance.now();
    state.isProgrammaticScroll = true;
    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / SLIDE_DURATION_MS);
      track.scrollLeft = from + distance * easeOutCubic(progress);
      if (progress < 1) {
        state.scrollAnim = window.requestAnimationFrame(step);
      } else {
        state.scrollAnim = null;
        finish();
      }
    };
    state.scrollAnim = window.requestAnimationFrame(step);
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // The progress-bar fill is driven entirely in JS (rAF), not via a CSS width
  // transition: a `transition` on a var()-driven `width` does NOT update in
  // Chrome when only the custom property changes — it leaves the fill stuck.
  const setProgress = (state, pct) => {
    if (state.progressAnim) {
      window.cancelAnimationFrame(state.progressAnim);
      state.progressAnim = null;
    }
    state.progressValue = pct;
    if (state.progressBar) {
      state.progressBar.style.setProperty('--card-image-progress', `${pct}%`);
    }
  };

  const animateProgressTo = (state, pct) => {
    if (!state.progressBar) return;
    if (state.progressAnim) {
      window.cancelAnimationFrame(state.progressAnim);
      state.progressAnim = null;
    }
    const from = state.progressValue || 0;
    if (Math.abs(pct - from) < 0.5) {
      setProgress(state, pct);
      return;
    }
    const startTime = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / SLIDE_DURATION_MS);
      const val = from + (pct - from) * easeOutCubic(t);
      state.progressValue = val;
      state.progressBar.style.setProperty('--card-image-progress', `${val}%`);
      if (t < 1) {
        state.progressAnim = window.requestAnimationFrame(tick);
      } else {
        state.progressAnim = null;
        state.progressValue = pct;
      }
    };
    state.progressAnim = window.requestAnimationFrame(tick);
  };

  const findNearestSlideIndex = (track, slides, fallbackIndex = 0) => {
    const trackRect = track.getBoundingClientRect();
    if (!trackRect || trackRect.width === 0) return fallbackIndex;

    let nearestIndex = fallbackIndex;
    let nearestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      if (!slideRect || slideRect.width === 0) return;

      const distance = Math.abs(slideRect.left - trackRect.left);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  };

  const updateCarouselUi = (state, instant) => {
    if (state.prevButton) {
      state.prevButton.disabled = state.currentIndex <= 0;
    }

    if (state.nextButton) {
      state.nextButton.disabled = state.currentIndex >= state.slides.length - 1;
    }

    // Continuous fill bar (Bruna-style): the fill reflects how far through the
    // images we are. First image → 0% (empty), last → 100%; wraps to empty on the
    // loop. Animated in JS unless an instant set is requested.
    const total = state.slides.length;
    const pct = total > 1 ? (state.currentIndex / (total - 1)) * 100 : 0;
    if (instant || reducedMotion.matches) {
      setProgress(state, pct);
    } else {
      animateProgressTo(state, pct);
    }
  };

  const preloadImageForSlide = (state, slide) => {
    if (!slide) return;
    const image = slide.querySelector('img');
    if (!image) return;

    if (image.complete && image.naturalWidth > 0) {
      slide.dataset.cardNextPreloadDone = 'true';
      return;
    }

    image.loading = 'eager';
    image.decoding = 'async';
    if ('fetchPriority' in image) image.fetchPriority = 'low';

    const requestKey = slide.dataset.cardCarouselSlide || image.currentSrc || image.src;
    if (!requestKey || state.preloadImageRequests.has(requestKey)) return;

    const preloadImage = new Image();
    state.preloadImageRequests.set(requestKey, preloadImage);

    const cleanup = () => {
      slide.dataset.cardNextPreloadDone = 'true';
      state.preloadImageRequests.delete(requestKey);
    };
    preloadImage.onload = cleanup;
    preloadImage.onerror = cleanup;

    try {
      preloadImage.decoding = 'async';
    } catch (_) {}
    if ('fetchPriority' in preloadImage) preloadImage.fetchPriority = 'low';

    if (image.sizes) preloadImage.sizes = image.sizes;
    if (image.srcset) preloadImage.srcset = image.srcset;
    preloadImage.src = image.currentSrc || image.src;
  };

  const preloadSlideAtIndex = (state, index) => {
    if (!state?.slides) return;
    if (index < 0 || index >= state.slides.length) return;

    const slide = state.slides[index];
    if (!slide || slide.dataset.cardNextPreloadDone === 'true') return;
    preloadImageForSlide(state, slide);
  };

  const preloadNextSlide = (state, currentIndex = state.currentIndex) => {
    if (!state?.slides || state.slides.length < 2) return;

    const nextIndex = currentIndex + 1;
    preloadSlideAtIndex(state, nextIndex);
  };

  const primePreloadForDirection = (state, direction) => {
    if (!state?.slides || state.slides.length < 2) return;
    if (direction !== 1 && direction !== -1) return;

    const lastIndex = state.slides.length - 1;
    const targetIndex = clamp(state.currentIndex + direction, 0, lastIndex);
    const lookAheadIndex = clamp(targetIndex + direction, 0, lastIndex);

    preloadSlideAtIndex(state, targetIndex);
    preloadSlideAtIndex(state, lookAheadIndex);
  };

  const scrollToSlide = (state, targetIndex, behavior = 'smooth') => {
    const nextIndex = clamp(targetIndex, 0, state.slides.length - 1);
    const targetSlide = state.slides[nextIndex];
    if (!targetSlide) return;

    state.currentIndex = nextIndex;
    // Smooth scroll → animate the fill; instant jumps (snap/normalise) → set it
    // instantly so the bar stays in lockstep with the scroll position.
    updateCarouselUi(state, behavior !== 'smooth');

    if (behavior === 'smooth') {
      animateScroll(state, targetSlide.offsetLeft);
    } else {
      if (state.scrollAnim) {
        window.cancelAnimationFrame(state.scrollAnim);
        state.scrollAnim = null;
      }
      state.isProgrammaticScroll = false;
      try {
        state.track.scrollTo({ left: targetSlide.offsetLeft, behavior: 'auto' });
      } catch (_) {
        state.track.scrollLeft = targetSlide.offsetLeft;
      }
    }

    preloadNextSlide(state, nextIndex);
  };

  const stopAutoCycle = (state) => {
    if (state.autoCycleTimer) {
      window.clearInterval(state.autoCycleTimer);
      state.autoCycleTimer = null;
    }
    if (state.progressBar) state.progressBar.classList.remove('is-cycling');
  };

  // Desktop hover: walk the images on a continuous loop, ~1s each. The fill bar
  // steps forward one image at a time (the `is-cycling` class just keeps the bar
  // visible while cycling). The wrap back to the first image is instant so it
  // doesn't read as a long reverse slide.
  const startAutoCycle = (state) => {
    if (state.autoCycleTimer || state.slides.length < 2) return;
    if (state.progressBar) state.progressBar.classList.add('is-cycling');

    state.autoCycleTimer = window.setInterval(() => {
      const lastIndex = state.slides.length - 1;
      if (state.currentIndex < lastIndex) {
        const nextIndex = state.currentIndex + 1;
        preloadSlideAtIndex(state, nextIndex);
        scrollToSlide(state, nextIndex, 'smooth');
        return;
      }

      // At the last image — loop seamlessly into the appended clone of the first
      // slide, then silently snap back to the real first (invisible: same pixels),
      // so the last→first step is a smooth forward slide, not a hard cut.
      if (state.cloneSlide) {
        // Reset the bar to empty UP FRONT (instant) so the first image slides in
        // already empty. The image glides forward into the clone, then snaps to
        // the real first. Keep the scroll handler suppressed across that snap so
        // its scroll events can't flip the index back to the last slide and
        // re-trigger the loop (which flickered the bar).
        state.currentIndex = 0;
        setProgress(state, 0);
        if (state.snapTimer) {
          window.clearTimeout(state.snapTimer);
          state.snapTimer = null;
        }
        animateScroll(state, state.cloneSlide.offsetLeft, () => {
          state.track.scrollLeft = state.slides[0].offsetLeft;
          state.currentIndex = 0;
          setProgress(state, 0);
          if (state.snapTimer) {
            window.clearTimeout(state.snapTimer);
            state.snapTimer = null;
          }
          window.setTimeout(() => {
            state.isProgrammaticScroll = false;
          }, 80);
        });
      } else {
        scrollToSlide(state, 0, 'auto');
      }
    }, AUTO_CYCLE_INTERVAL_MS);
  };

  const initCardCarousel = (carousel) => {
    if (!(carousel instanceof HTMLElement) || carouselStates.has(carousel)) return;

    const track = carousel.querySelector('[data-card-carousel-track]');
    const slides = Array.from(carousel.querySelectorAll('[data-card-carousel-slide]'));
    if (!track || slides.length < 2) return;

    // The JS rAF tweens are the sole source of smooth scrolling — disable the
    // CSS `scroll-behavior: smooth` so programmatic scrollLeft sets are INSTANT.
    // Otherwise the loop's snap-back (clone → first) smooth-scrolls backward
    // through every slide and the scroll handler flickers the bar descending.
    track.style.scrollBehavior = 'auto';

    const state = {
      track,
      slides,
      prevButton: carousel.querySelector('[data-card-carousel-prev]'),
      nextButton: carousel.querySelector('[data-card-carousel-next]'),
      progressBar: carousel.querySelector('[data-card-carousel-progress]'),
      currentIndex: 0,
      autoCycleTimer: null,
      scrollAnim: null,
      isProgrammaticScroll: false,
      progressValue: 0,
      progressAnim: null,
      cloneSlide: null,
      ticking: false,
      snapTimer: null,
      preloadImageRequests: new Map(),
      hoverPreviewActive: false,
      userNavigatedInHover: false,
      pointerStartX: 0,
      pointerStartY: 0,
      pointerStartIndex: 0,
      isSwiping: false,
      pointerIsDown: false,
      primedSwipeDirection: 0,
    };

    carouselStates.set(carousel, state);

    // For the seamless auto-cycle loop (hover-capable only), append a clone of
    // the first slide after the last. Stepping "past" the last image slides into
    // this duplicate, then we snap back to the real first slide invisibly (same
    // pixels), so last→first is a smooth forward slide instead of a hard jump.
    if (hoverCapableMediaQuery.matches && slides.length >= 2) {
      const clone = slides[0].cloneNode(true);
      clone.setAttribute('data-card-carousel-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.removeAttribute('data-card-carousel-slide');
      clone.style.scrollSnapAlign = 'none';
      track.appendChild(clone);
      state.cloneSlide = clone;
    }

    const normalizeSnapPosition = () => {
      if (state.pointerIsDown) return;
      const nearestIndex = findNearestSlideIndex(track, slides, state.currentIndex);
      scrollToSlide(state, nearestIndex, 'auto');
    };

    const resetToFirstSlide = () => {
      state.currentIndex = 0;
      updateCarouselUi(state, true);

      const firstSlide = state.slides[0];
      if (!firstSlide) return;
      const firstOffset = firstSlide.offsetLeft || 0;

      try {
        state.track.scrollTo({
          left: firstOffset,
          behavior: 'auto',
        });
      } catch (_) {
        state.track.scrollLeft = firstOffset;
      }
    };

    resetToFirstSlide();
    requestAnimationFrame(resetToFirstSlide);
    requestAnimationFrame(normalizeSnapPosition);
    window.setTimeout(normalizeSnapPosition, 120);
    if (document.readyState === 'complete') {
      normalizeSnapPosition();
    } else {
      window.addEventListener('load', normalizeSnapPosition, { once: true });
    }

    track.addEventListener(
      'scroll',
      () => {
        // Ignore scroll events fired by our own JS slide tween — it sets the
        // index/UI itself; letting the handler re-snap would fight the tween.
        if (state.isProgrammaticScroll) return;
        if (state.ticking) return;
        state.ticking = true;

        requestAnimationFrame(() => {
          // Ignore reads while scrolled into the clone region (past the last real
          // slide): findNearestSlideIndex would return the last slide and could
          // re-trigger the loop / flicker the bar.
          if (
            state.cloneSlide &&
            track.scrollLeft > state.slides[state.slides.length - 1].offsetLeft + 4
          ) {
            state.ticking = false;
            return;
          }
          const nearestIndex = findNearestSlideIndex(track, slides, state.currentIndex);
          if (nearestIndex !== state.currentIndex) {
            state.currentIndex = nearestIndex;
            updateCarouselUi(state, true);
            preloadNextSlide(state, nearestIndex);
          }

          if (state.snapTimer) {
            window.clearTimeout(state.snapTimer);
          }
          state.snapTimer = window.setTimeout(() => {
            if (state.pointerIsDown) return;
            scrollToSlide(state, state.currentIndex, 'auto');
          }, 90);

          state.ticking = false;
        });
      },
      { passive: true }
    );

    track.addEventListener(
      'wheel',
      (event) => {
        if (!hoverCapableMediaQuery.matches) return;

        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);
        const isHorizontalGesture = absX > absY || (event.shiftKey && absY > 0);
        if (!isHorizontalGesture) return;

        const delta = absX > 0 ? event.deltaX : event.deltaY;
        if (delta === 0) return;

        stopAutoCycle(state);
        primePreloadForDirection(state, delta > 0 ? 1 : -1);
        preloadNextSlide(state, state.currentIndex);
        state.userNavigatedInHover = true;
        state.hoverPreviewActive = false;
        announceSlideshowUse();
        event.preventDefault();
        track.scrollLeft += delta;
      },
      { passive: false }
    );

    if (state.prevButton) {
      state.prevButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        stopAutoCycle(state);
        state.userNavigatedInHover = true;
        state.hoverPreviewActive = false;
        announceSlideshowUse();
        scrollToSlide(state, state.currentIndex - 1);
      });
    }

    if (state.nextButton) {
      state.nextButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        stopAutoCycle(state);
        state.userNavigatedInHover = true;
        state.hoverPreviewActive = false;
        announceSlideshowUse();
        scrollToSlide(state, state.currentIndex + 1);
      });
    }

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse') return;
        preloadNextSlide(state, state.currentIndex);
        state.primedSwipeDirection = 0;
        state.pointerIsDown = true;
        state.pointerStartX = event.clientX;
        state.pointerStartY = event.clientY;
        state.pointerStartIndex = state.currentIndex;
        state.isSwiping = false;
        if (typeof track.setPointerCapture === 'function' && typeof event.pointerId === 'number') {
          try {
            track.setPointerCapture(event.pointerId);
          } catch (_) {}
        }
      },
      { passive: true }
    );

    track.addEventListener(
      'touchstart',
      () => {
        preloadNextSlide(state, state.currentIndex);
      },
      { passive: true }
    );

    track.addEventListener(
      'pointermove',
      (event) => {
        if (!state.pointerIsDown || event.pointerType === 'mouse') return;

        const deltaX = event.clientX - state.pointerStartX;
        const deltaY = event.clientY - state.pointerStartY;

        if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
          state.isSwiping = true;

          const swipeDirection = deltaX < 0 ? 1 : -1;
          if (swipeDirection !== state.primedSwipeDirection) {
            state.primedSwipeDirection = swipeDirection;
            primePreloadForDirection(state, swipeDirection);
          }
        }
      },
      { passive: true }
    );

    const clearPointerState = () => {
      if (!state.pointerIsDown) return;
      state.pointerIsDown = false;

      if (state.isSwiping) {
        state.userNavigatedInHover = true;
        state.hoverPreviewActive = false;
        announceSlideshowUse();
      }

      // Loop (touch only): a forward swipe that STARTED on the last image wraps
      // back to the first — mobile has no next arrow or clone, so otherwise the
      // gallery dead-ends on the last image. Capture before the reset below.
      const wasForwardSwipeAtEnd =
        state.isSwiping &&
        state.primedSwipeDirection === 1 &&
        state.pointerStartIndex === state.slides.length - 1;

      state.primedSwipeDirection = 0;
      window.setTimeout(() => {
        state.isSwiping = false;
      }, 0);

      if (wasForwardSwipeAtEnd) {
        scrollToSlide(state, 0, 'auto');
        return;
      }

      const nearestIndex = findNearestSlideIndex(track, slides, state.currentIndex);
      scrollToSlide(state, nearestIndex, 'auto');
    };

    track.addEventListener('pointerup', clearPointerState, { passive: true });
    track.addEventListener('pointercancel', clearPointerState, { passive: true });
    track.addEventListener('lostpointercapture', clearPointerState, { passive: true });
    track.addEventListener('touchend', clearPointerState, { passive: true });
    track.addEventListener('touchcancel', clearPointerState, { passive: true });

    track.addEventListener(
      'click',
      (event) => {
        if (!state.isSwiping) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    carousel.addEventListener('mouseenter', () => {
      if (!hoverCapableMediaQuery.matches) return;
      preloadSlideAtIndex(state, state.currentIndex + 1);
      startAutoCycle(state);
    });

    carousel.addEventListener('mouseleave', () => {
      if (!hoverCapableMediaQuery.matches) return;
      stopAutoCycle(state);
      // Settle back on the featured image when the pointer leaves.
      scrollToSlide(state, 0, 'auto');
    });
  };

  const initCardCarousels = (root = document) => {
    if (!(root instanceof Element) && root !== document) return;
    const scope = root === document ? document : root;
    scope.querySelectorAll(carouselSelector).forEach(initCardCarousel);
  };

  initCardCarousels();

  const carouselObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        if (node.matches(carouselSelector)) {
          initCardCarousel(node);
        }

        node.querySelectorAll(carouselSelector).forEach(initCardCarousel);
      });
    });
  });

  carouselObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCardCarouselRuntime, { once: true });
} else {
  initCardCarouselRuntime();
}
